
import { defineComponent, ref, onMounted, computed } from 'vue'
import LoadingButton from '@/views/components/LoadingButton.vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import { Kamoku } from '@/repositories/fuzoku-meisai/data'
import { junsokuRepositories } from '@/repositories/fuzoku-meisai/junsoku/junsokuRepositories'
import { callApiWrapper } from '@/views/callApiWrapper'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import { KashiKariLongUiItem, KashiKariShortUiItem, LoanTermType } from '@/views/fuzoku-meisai/junsoku/data'
import { UiItemsLogic } from '@/views/fuzoku-meisai/uiItemsLogic'
import KashitsukekinSettingLongItem from '@/views/fuzoku-meisai/junsoku/KashitsukekinSettingLongItem.vue'
import KashitsukekinSettingShortItem from '@/views/fuzoku-meisai/junsoku/KashitsukekinSettingShortItem.vue'
import { PostParam } from '@/repositories/fuzoku-meisai/junsoku/kashitsukekinRepository'
import JunsokuSettingTitle from '@/views/fuzoku-meisai/junsoku/JunsokuSettingTitle.vue'

const repository = junsokuRepositories.kashitsukekin

export default defineComponent({
  components: {
    JunsokuSettingTitle,
    KashitsukekinSettingShortItem,
    KashitsukekinSettingLongItem,
    ResponseResultAlert,
    ContentLayout,
    LoadingButton
  },
  setup () {
    let kamokuSource: Kamoku[] = []
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)

    const longItems = ref<KashiKariLongUiItem[]>([])
    const shortItems = ref<KashiKariShortUiItem[]>([])

    const isAllValid = computed(() => {
      const allCount = longItems.value.length + shortItems.value.length
      if (allCount === 0) {
        return true
      }
      const allValidCount = longItems.value.filter(i => i.isValid).length + shortItems.value.filter(i => i.isValid).length
      return allCount === allValidCount
    })

    onMounted(async () => {
      loading.value = true
      await callApiWrapper(requestError, async () => {
        await refreshUiData()
      })
      loading.value = false
    })

    const onClickRegister = async () => {
      loading.value = true
      onDismissAlert()

      await callApiWrapper(requestError, async () => {
        const postParams: PostParam = {
          longItems: longItems.value.map(v => ({
            itemName: v.item.itemName,
            afterYearsSetting: v.item.afterYearsSetting.targets,
            withinOneYearSetting: v.item.withinOneYearSetting.targets
          })),
          shortItems: shortItems.value.map(v => ({
            itemName: v.item.itemName,
            setting: v.item.setting.targets
          }))
        }

        await repository.save(postParams)
        await refreshUiData()
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    const addLongItem = () => {
      const newId = UiItemsLogic.makeId(longItems.value)
      longItems.value.push({
        isValid: false,
        item: {
          id: newId,
          itemName: '',
          afterYearsSetting: {
            sources: [...kamokuSource],
            targets: []
          },
          withinOneYearSetting: {
            sources: [...kamokuSource],
            targets: []
          }
        }
      })
    }

    const addShortItem = () => {
      const newId = UiItemsLogic.makeId(shortItems.value)
      shortItems.value.push({
        isValid: false,
        item: {
          id: newId,
          itemName: '',
          setting: {
            sources: [...kamokuSource],
            targets: []
          }
        }
      })
    }

    const upItem = (type: LoanTermType, item: KashiKariLongUiItem | KashiKariShortUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.upUiItem(items, item)
    }

    const downItem = (type: LoanTermType, item: KashiKariLongUiItem | KashiKariShortUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.downUiItem(items, item)
    }

    const trashItem = (type: LoanTermType, item: KashiKariLongUiItem | KashiKariShortUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.removeUiItem(items, item)
    }

    const onChangeInput = (type: LoanTermType, item: KashiKariLongUiItem | KashiKariShortUiItem) => {
      const items = getUiItems(type)
      UiItemsLogic.updateUiItem(items, item)
    }

    const getUiItems = (type: LoanTermType) => {
      switch (type) {
        case 'Long':
          return longItems.value
        case 'Short':
          return shortItems.value
        default:
          throw Error('unknown type')
      }
    }

    const refreshUiData = async () => {
      const data = await repository.list()
      kamokuSource = data.kamokuSources
      longItems.value = data.longItems.map(value => ({ item: value, isValid: true }))
      shortItems.value = data.shortItems.map(value => ({ item: value, isValid: true }))
    }

    return {
      loading,
      isAllValid,
      isSucceedRequest,
      requestError,
      longItems,
      shortItems,
      onClickRegister,
      onDismissAlert,
      addLongItem,
      addShortItem,
      upItem,
      downItem,
      trashItem,
      onChangeInput
    }
  }
})
